import { useMemo, useState } from "react";
import { ExternalLink } from "lucide-react";
import { BarChart, Bar as RBar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PageHeader, Panel, Loading, ErrorBox } from "@/components/Widgets";
import { useApi, nf, df } from "@/lib/api";

const FILTERS = [["all", "Tutti"], ["instagram", "Instagram"], ["facebook", "Facebook"]];
const SORTS = [["timestamp", "Data"], ["interactions", "Interazioni"], ["reach", "Copertura"], ["engagement_rate", "ER"], ["saves", "Salvataggi"], ["shares", "Condivisioni"]];

const Heatmap = ({ cells }) => {
  const max = Math.max(...cells.map((c) => c.score), 1);
  const days = [...new Set(cells.map((c) => c.day))];
  const hours = [...new Set(cells.map((c) => c.hour))];
  return (
    <div className="overflow-x-auto" data-testid="heatmap">
      <table className="text-[11px] font-mono-num">
        <thead><tr><th />{hours.map((h) => <th key={h} className="px-1 pb-1 font-normal text-[#5C5C58]">{String(h).padStart(2, "0")}</th>)}</tr></thead>
        <tbody>{days.map((d) => (
          <tr key={d}><td className="pr-2 text-[#5C5C58]">{d}</td>
            {hours.map((h) => { const c = cells.find((x) => x.day === d && x.hour === h); return (
              <td key={h} className="p-0.5"><div title={`${c.count} post · ER ${c.score}%`} className="w-9 h-7 border border-[#E8E8E4]" style={{ background: c.count ? `rgba(42,75,60,${0.12 + (c.score / max) * 0.88})` : "#FAFAF8" }} /></td>
            ); })}
          </tr>))}
        </tbody>
      </table>
      <p className="text-xs text-[#5C5C58] mt-3">Fasce di 3 ore. Più scuro = engagement rate medio più alto.</p>
    </div>
  );
};

export default function Content() {
  const { data, error, loading } = useApi("/content");
  const [platform, setPlatform] = useState("all");
  const [sort, setSort] = useState("timestamp");
  const rows = useMemo(() => {
    if (!data) return [];
    const r = data.media.filter((m) => platform === "all" || m.platform === platform);
    return [...r].sort((a, b) => (sort === "timestamp" ? (b.timestamp || "").localeCompare(a.timestamp || "") : b[sort] - a[sort]));
  }, [data, platform, sort]);
  if (loading) return <Loading />;
  if (error) return <ErrorBox msg={error} />;
  return (
    <>
      <PageHeader eyebrow={`${data.media.length} contenuti analizzati · ${data.posts_per_week} a settimana`} title="Post & Reel" desc="Reazioni, commenti, salvataggi e condivisioni di ogni contenuto, con i momenti migliori per pubblicare." />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
        <Panel testid="heatmap-panel" title="Quando il tuo pubblico interagisce" sub="Engagement per giorno e fascia oraria"><Heatmap cells={data.heatmap} /></Panel>
        <Panel testid="weekday-panel" title="Interazioni medie per giorno">
          <div className="h-60"><ResponsiveContainer><BarChart data={data.weekday}><CartesianGrid stroke="#F1F1EC" vertical={false} /><XAxis dataKey="day" tickLine={false} axisLine={false} /><YAxis tickLine={false} axisLine={false} width={35} /><Tooltip /><RBar dataKey="avg_interactions" name="Interazioni medie" fill="#2A4B3C" /></BarChart></ResponsiveContainer></div>
        </Panel>
      </div>
      <Panel testid="hashtag-perf" title="Hashtag che usi e quanto rendono" sub="Interazioni medie dei post che li contengono" className="mb-4">
        <div className="flex flex-wrap gap-2">
          {data.hashtags.map((h) => (
            <span key={h.tag} className="border border-[#E8E8E4] px-2.5 py-1 text-xs hover:border-[#2A4B3C] transition-colors">#{h.tag} <span className="font-mono-num text-[#2A4B3C] ml-1">{nf(h.avg_interactions)}</span><span className="text-[#5C5C58]"> · {h.uses}x</span></span>
          ))}
          {!data.hashtags.length && <span className="text-sm text-[#5C5C58]">Nessun hashtag trovato nelle caption.</span>}
        </div>
      </Panel>
      <Panel testid="content-table" title="Tutti i contenuti" action={
        <div className="flex gap-3 flex-wrap">
          <div className="flex border border-[#E8E8E4]">{FILTERS.map(([k, l]) => <button key={k} data-testid={`filter-${k}`} onClick={() => setPlatform(k)} className={`px-3 py-1.5 text-xs transition-colors ${platform === k ? "bg-[#2A4B3C] text-white" : "hover:bg-[#F7F7F5]"}`}>{l}</button>)}</div>
          <select data-testid="sort-select" value={sort} onChange={(e) => setSort(e.target.value)} className="border border-[#E8E8E4] text-xs px-2 bg-white">{SORTS.map(([k, l]) => <option key={k} value={k}>Ordina: {l}</option>)}</select>
        </div>}>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader><TableRow>{["Contenuto", "Formato", "Data", "Copertura", "Reazioni", "Commenti", "Salvati", "Condivisi", "ER", ""].map((h) => <TableHead key={h} className="label !text-[10px]">{h}</TableHead>)}</TableRow></TableHeader>
            <TableBody>
              {rows.map((m) => (
                <TableRow key={m.id} data-testid={`content-row-${m.id}`}>
                  <TableCell className="max-w-[320px]"><div className="flex items-center gap-3">{m.thumbnail && <img src={m.thumbnail} alt="" className="w-10 h-10 object-cover shrink-0" />}<span className="truncate text-sm">{m.caption || "(senza testo)"}</span></div></TableCell>
                  <TableCell><span className={`text-[10px] font-semibold px-1.5 py-0.5 border ${m.platform === "instagram" ? "border-[#2A4B3C] text-[#2A4B3C]" : "border-[#C05C3D] text-[#C05C3D]"}`}>{m.platform === "instagram" ? "IG" : "FB"} {m.format}</span></TableCell>
                  <TableCell className="text-xs whitespace-nowrap">{df(m.timestamp)}</TableCell>
                  {["reach", "likes", "comments", "saves", "shares"].map((k) => <TableCell key={k} className="font-mono-num text-sm">{nf(m[k])}</TableCell>)}
                  <TableCell className="font-mono-num text-sm font-semibold">{m.engagement_rate}%</TableCell>
                  <TableCell>{m.permalink && <a href={m.permalink} target="_blank" rel="noreferrer" data-testid={`open-${m.id}`}><ExternalLink className="w-3.5 h-3.5 text-[#5C5C58] hover:text-[#2A4B3C]" /></a>}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Panel>
    </>
  );
}
